// Pure placement maths for roster action menus (#477).
//
// Kept free of DOM access so it can be unit-tested with `node --test`; the
// hook in dropdown-position.js does the measuring and style writing.

export const GAP = 4

// Below this a panel shows less than ~two action rows, at which point capping
// it to the leftover sliver is worse than spanning the whole visible area.
export const MIN_USABLE_HEIGHT = 64

/**
 * Place a viewport-anchored panel directly above or below its anchor.
 *
 * Unlike roster menus, this mode intentionally ignores clipping ancestors:
 * the panel uses `position: fixed`, so viewport space is the only vertical
 * constraint. Keeping the calculation here also makes DOM order irrelevant.
 *
 * @returns {{placement: "below"|"above", maxHeight: number|null, top: number}}
 */
export function computeFixedPlacement({
  anchorTop,
  anchorBottom,
  panelHeight,
  viewportTop = 0,
  viewportBottom,
  gap = GAP,
}) {
  // Scrolling can carry the anchor under the sticky banner, or off the top of
  // the window entirely. Both edges of the below branch clamp to the visible
  // region: measuring down from a hidden anchor claims room the banner already
  // covers, and an unclamped `top` puts the panel itself behind it — the panel
  // is the lower stacking layer, so its first rows stop being clickable, and a
  // panel that "fits" gets no internal scroll to bring them back.
  const spaceBelow = Math.max(viewportBottom - Math.max(anchorBottom, viewportTop) - gap, 0)
  const spaceAbove = Math.max(anchorTop - gap - viewportTop, 0)
  const placement = panelHeight <= spaceBelow || spaceBelow >= spaceAbove ? "below" : "above"
  const available = placement === "below" ? spaceBelow : spaceAbove
  const maxHeight = panelHeight <= available ? null : available
  const renderedHeight = maxHeight === null ? panelHeight : maxHeight
  // The above branch needs no clamp: `spaceAbove` already stops at viewportTop,
  // so a capped panel lands exactly on it and an uncapped one starts below it.
  const top =
    placement === "below"
      ? Math.max(anchorBottom + gap, viewportTop)
      : anchorTop - gap - renderedHeight

  return { placement, maxHeight, top }
}

/**
 * Intersect every clipping rectangle into the region the panel may occupy.
 * Clipping is cumulative — an inner scroller does not widen what an outer
 * modal or the viewport already cut off.
 *
 * @param {Array<{top: number, bottom: number}>} rects
 * @returns {{top: number, bottom: number}}
 */
export function intersectBounds(rects) {
  return rects.reduce(
    (acc, rect) => ({
      top: Math.max(acc.top, rect.top),
      bottom: Math.min(acc.bottom, rect.bottom),
    }),
    { top: -Infinity, bottom: Infinity },
  )
}

/**
 * Decide where the panel goes.
 *
 * All coordinates are viewport-relative, matching getBoundingClientRect.
 * `naturalTop` is where the panel actually sits when left alone, measured
 * rather than derived from the `top-5` class, so a taller-than-usual trigger
 * (a two-line salary cell, say) can't skew the estimate.
 *
 * @returns {{placement: "below"|"above"|"fill", maxHeight: number|null, offsetTop: number|null}}
 *   `maxHeight` is null when the panel fits without scrolling internally.
 *   `offsetTop` is only set for "fill", as a viewport-relative target top.
 */
export function computePlacement({
  anchorTop,
  naturalTop,
  panelHeight,
  boundsTop,
  boundsBottom,
  gap = GAP,
  minUsableHeight = MIN_USABLE_HEIGHT,
}) {
  const spaceBelow = boundsBottom - naturalTop
  const spaceAbove = anchorTop - gap - boundsTop

  if (panelHeight <= spaceBelow) {
    return { placement: "below", maxHeight: null, offsetTop: null }
  }

  if (panelHeight <= spaceAbove) {
    return { placement: "above", maxHeight: null, offsetTop: null }
  }

  // Neither side fits. Prefer the roomier one, but never claim more room than
  // was measured — an over-tall panel just pushes its own content back outside
  // the clip region where it cannot be scrolled into view.
  const best = Math.max(spaceBelow, spaceAbove, 0)

  if (best < minUsableHeight) {
    const available = Math.max(boundsBottom - boundsTop, 0)
    return { placement: "fill", maxHeight: available, offsetTop: boundsTop }
  }

  return {
    placement: spaceAbove > spaceBelow ? "above" : "below",
    maxHeight: best,
    offsetTop: null,
  }
}
